import React from 'react';
import { filingTargetLabel } from '~/components/ams-email-log/client-list';
import { Spinner } from '@oysterjs/ui/spinner';

import { EmailAmsMatchOutcome } from '~/api/graphql';
import { integrationName } from '~/lib/integrations';
import type {
  EmailAmsChoice,
  EmailAmsMatch
} from '~/components/workspaces/hooks/use-email-ams-match';

const ACTION_CLASS =
  'font-medium whitespace-nowrap underline text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300';
const AMBER_TEXT = 'text-amber-800 dark:text-amber-300';
const PLAIN_TEXT = 'text-neutral-600 dark:text-neutral-300';

export const LineAction = (props: { onClick: () => void; children: React.ReactNode }) => (
  <button type="button" className={ACTION_CLASS} onClick={props.onClick}>
    {props.children}
  </button>
);

const ClientName = (props: { children: React.ReactNode }) => (
  <strong className="font-semibold text-neutral-900 dark:text-neutral-100">{props.children}</strong>
);

export type EmailAmsLineProps = {
  /** The latest match, or null before the first answer or when no AMS logs emails. */
  match: EmailAmsMatch | null;
  choice: EmailAmsChoice | null;
  isMatching: boolean;
  hasFailed: boolean;
  toAddressCount: number;
  isScheduled: boolean;
  /** False for a reader who can't change this email: the line names no actions. */
  canChoose: boolean;
  onRetry: () => void;
  onSkip: () => void;
  onUndoSkip: () => void;
  /** Opens Choose client. Without it, the line offers no way to choose. */
  onChooseClient?: () => void;
};

/**
 * Where the email will be logged in the AMS when it sends, under To. Nothing until the
 * first match answers, because until then the AMS is unknown and most agencies log
 * nowhere, so the error and Try again show only after a first answer.
 */
export const EmailAmsLine = (props: EmailAmsLineProps) => {
  const { match, choice, canChoose, onChooseClient } = props;
  if (!match) return null;

  const amsName = integrationName(match.integrationType);
  const thisAddress = props.toAddressCount === 1 ? 'this address' : 'these addresses';
  const chooseClient = canChoose ? onChooseClient : undefined;

  const willBeLoggedTo = (clientName: string) => (
    <>
      <span className={PLAIN_TEXT}>
        Will be logged to <ClientName>{clientName}</ClientName> in {amsName}
        {props.isScheduled ? ' when it sends' : ''}.
      </span>
      {canChoose && (
        <>
          {chooseClient && <LineAction onClick={chooseClient}>Change</LineAction>}
          <LineAction onClick={props.onSkip}>Don&apos;t log this email</LineAction>
        </>
      )}
    </>
  );

  const line = (): React.ReactNode => {
    if (props.isMatching) {
      return (
        <span className="inline-flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
          <Spinner size="size-3.5" color="neutral" />
          Looking for {thisAddress} in {amsName}…
        </span>
      );
    }
    if (props.hasFailed) {
      return (
        <>
          <span className={AMBER_TEXT}>
            Couldn&apos;t check {amsName} for {thisAddress}.
          </span>
          {canChoose && <LineAction onClick={props.onRetry}>Try again</LineAction>}
        </>
      );
    }
    if (!match.isSupported) {
      return (
        <span className="text-neutral-500 dark:text-neutral-400">
          Not logged. {amsName} isn&apos;t supported yet.
        </span>
      );
    }
    if (choice && !choice.target) {
      return (
        <>
          <span className={PLAIN_TEXT}>This email won&apos;t be logged.</span>
          {canChoose && <LineAction onClick={props.onUndoSkip}>Undo</LineAction>}
        </>
      );
    }
    if (choice?.displayName) {
      return willBeLoggedTo(
        choice.filingTarget
          ? `${choice.displayName} › ${filingTargetLabel(choice.filingTarget)}`
          : choice.displayName
      );
    }
    const clientCount = match.candidates.length;
    switch (match.outcome) {
      case EmailAmsMatchOutcome.OneMatch:
        return willBeLoggedTo(match.candidates[0].displayName);
      case EmailAmsMatchOutcome.SeveralMatch:
        return chooseClient ? (
          <span className={AMBER_TEXT}>
            {clientCount} clients match this address.{' '}
            <LineAction onClick={chooseClient}>Choose client</LineAction> to log this email.
          </span>
        ) : (
          <span className={AMBER_TEXT}>
            {clientCount} clients match this address. This email won&apos;t be logged unless someone
            chooses one.
          </span>
        );
      case EmailAmsMatchOutcome.RecipientsMatchSeveral:
        return (
          <>
            <span className={AMBER_TEXT}>These recipients match {clientCount} clients.</span>
            {chooseClient && <LineAction onClick={chooseClient}>Choose client</LineAction>}
          </>
        );
      case EmailAmsMatchOutcome.NoMatch:
        return (
          <>
            <span className={PLAIN_TEXT}>
              No client in {amsName} matches {thisAddress}.
            </span>
            {chooseClient && <LineAction onClick={chooseClient}>Choose client</LineAction>}
          </>
        );
    }
  };

  // The blank column lines the text up with the row's control, past its label.
  return (
    <div className="flex gap-2 pt-0.5 text-sm/6">
      <span className="w-32 shrink-0" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">{line()}</div>
    </div>
  );
};
